/** Where this kingdom's portals come out, learned by walking through them.
 *
 * `computeReachable` will route across a subterranean gate the moment it is
 * handed a `PortalLink`, and a link is a pair of mouth cells. The pairing lives
 * in the object's *instance* payload (`channel`), and an observation carries no
 * instance payload at all - by design, because a payload is what the fog is
 * hiding. So the planner cannot read which mouth answers which, and it must not
 * guess: two subterranean gates on one map share a def and an art, and pairing
 * them by looks would send an army down a hole and out somewhere else.
 *
 * What it can do is remember. A hero who walks into a mouth comes out of one,
 * and the observation shows him standing there - that is a fact the player has
 * *seen*, which is exactly the standard I9 sets. So this file records the one
 * thing the AI is entitled to know about a gate: where its own heroes have
 * come out of it. The first trip through is scouting (`GATE_LEVEL_VALUE` prices
 * the door for that); every trip after it is a road the route planner can cost.
 *
 * The consequence worth stating plainly: an unused gate is invisible to the
 * planner, and that is the correct answer rather than a limitation. A gate
 * nobody has been through is, to the player, a hole in the ground.
 *
 * When `ObservedObject` grows a `channel` - which is the one field that would
 * let a kingdom read a pairing off the map the way it reads a town's faction
 * off its silhouette - `portalLinks(mouths)` in `map/path.ts` replaces the
 * learning half of this file and the rest of it stays as it is.
 */

import { cellIndex } from "@game/core";
import type {
  ContentRegistry, HeroId, ObservedObject, PlayerObservation, PortalLink,
} from "@game/core";

/**
 * One kingdom's portal knowledge, carried across turns by its controller.
 *
 * Mutable on purpose: it is the controller's memory, not game state, and it is
 * never hashed, saved or replayed. Everything read out of it is sorted before
 * it leaves, so no consumer can depend on the order transits happened in.
 */
export interface PortalMemory {
  /** Hero -> the mouth cell the last route we issued ended on. */
  readonly aimed: Map<string, number>;
  /**
   * Mouth cell -> the mouth cell a hero of ours came out at, or -1 once two
   * trips through the same mouth have come out in different places.
   */
  readonly exits: Map<number, number>;
}

export const newPortalMemory = (): PortalMemory => ({
  aimed: new Map<string, number>(),
  exits: new Map<number, number>(),
});

/**
 * The cell a hero stands on to use this portal.
 *
 * Not the anchor. A subterranean gate is a two-by-two gatehouse whose anchor is
 * a blocking footprint cell and whose entrance is at (1, 0); a link built on the
 * anchor is silently dropped by `canonicalLinks` as unwalkable, which is the
 * quiet way this whole mechanism would come to nothing. `arrivalCell` in
 * `objects.ts` resolves the far side the same way, so the cell recorded here is
 * the cell the kernel actually puts the hero on.
 */
export function mouthCell(
  obs: PlayerObservation, content: ContentRegistry, o: ObservedObject,
): number {
  const m = obs.map;
  const inMap = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < m.width && y < m.height;
  for (const [dx, dy] of content.mapObjects.get(o.def)?.entrances ?? []) {
    if (inMap(o.x + dx, o.y + dy)) return cellIndex(m, o.x + dx, o.y + dy, o.z);
  }
  return cellIndex(m, o.x, o.y, o.z);
}

/** Every portal mouth the observation is naming, cell -> object id. */
export function portalMouths(
  obs: PlayerObservation, content: ContentRegistry,
): ReadonlyMap<number, string> {
  const out = new Map<number, string>();
  for (const o of obs.objects) {
    if (o.kind !== "portal") continue;
    out.set(mouthCell(obs, content, o), o.id as string);
  }
  return out;
}

/**
 * Record that the route just issued for `hero` ends by stepping onto a mouth.
 *
 * Called with the command the controller is about to hand the kernel, so the
 * "from" half of a link is what the planner asked for rather than something
 * reconstructed afterwards. A route that ends anywhere else clears the note:
 * the hero is walking, not jumping.
 */
export function aimAtMouth(
  mem: PortalMemory, hero: HeroId, last: number, mouths: ReadonlyMap<number, string>,
): void {
  if (mouths.has(last)) mem.aimed.set(hero as string, last);
  else mem.aimed.delete(hero as string);
}

/**
 * Turn "the hero is not where we sent him" into a link, once.
 *
 * A hero standing on a mouth with a `CHOOSE` still open has not jumped yet, so
 * the note is kept until his cell actually changes. When it changes to another
 * mouth that is not a step away - a different plane, or across the map - the
 * only thing that can have moved him is the portal, and the pair is recorded.
 * Anything else clears the note without recording, because a walk off a mouth
 * teaches nothing.
 */
export function learnTransits(
  mem: PortalMemory, obs: PlayerObservation, content: ContentRegistry,
): void {
  if (mem.aimed.size === 0) return;
  const mouths = portalMouths(obs, content);
  const m = obs.map;
  const live = new Set<string>();
  for (const h of obs.heroes) {
    if (h.detail === null) continue;
    live.add(h.id as string);
    const from = mem.aimed.get(h.id as string);
    if (from === undefined) continue;
    const to = cellIndex(m, h.x, h.y, h.z);
    if (to === from) continue;
    mem.aimed.delete(h.id as string);
    if (!mouths.has(to)) continue;
    const plane = m.width * m.height;
    const fz = Math.trunc(from / plane), tz = Math.trunc(to / plane);
    const fr = from - fz * plane, tr = to - tz * plane;
    const adjacent = fz === tz
      && Math.abs((fr % m.width) - (tr % m.width)) <= 1
      && Math.abs(Math.trunc(fr / m.width) - Math.trunc(tr / m.width)) <= 1;
    if (adjacent) continue;
    const held = mem.exits.get(from);
    // Two answers from one mouth is a `CHOOSE` prompt, not a road; see
    // `knownLinks`. Marked rather than overwritten, so the later trip cannot
    // make the planner confident about a coin flip.
    mem.exits.set(from, held === undefined || held === to ? to : -1);
  }
  // A hero who is gone - defeated, or swallowed by a battle - cannot answer.
  for (const key of [...mem.aimed.keys()]) if (!live.has(key)) mem.aimed.delete(key);
}

/**
 * The links, canonically ordered.
 *
 * Sorted by `(from, to)` so the list is a function of what is known and never
 * of the order it was learned in - `computeReachable` re-sorts it anyway, and
 * `via` indexes the result, so an unsorted list here would be one more place a
 * replay could drift.
 *
 * A `from` we have come out of two different places is dropped: that is a
 * portal with a `CHOOSE` behind it, the answer belongs to the player rather
 * than to the route planner, and `canonicalLinks` would drop it for the same
 * reason one layer down. Recording only the last exit would quietly make the
 * planner confident about a coin flip.
 */
export function knownLinks(mem: PortalMemory): readonly PortalLink[] {
  const out: PortalLink[] = [];
  for (const from of [...mem.exits.keys()].sort((a, b) => a - b)) {
    const to = mem.exits.get(from)!;
    if (to >= 0) out.push({ from, to });
  }
  return out;
}
